import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListSubheader,
  Grid,
  CircularProgress,
  Chip,
  InputAdornment,
  ListItemButton,
  ListItemText,
  Checkbox,
  Collapse,
  InputBase,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import { fetchData } from "../api/client";

/**
 * Multi-select que renderiza a lista DENTRO do dialog (sem portal/Popper).
 * Garante que cliques nas opções funcionem em qualquer contexto (Modal/Dialog).
 */
function InlineMultiSelect({
  label,
  placeholder,
  options,
  value,
  onChange,
  getOptionLabel = (opt) => (opt?.nome ?? opt?.name ?? String(opt?.id ?? opt ?? "")),
  getOptionValue = (opt) => (opt?.id != null ? Number(opt.id) : opt),
  openKey,
  isOpen,
  onToggleOpen,
  searchPlaceholder = "Filtrar...",
}) {
  const [search, setSearch] = useState("");
  const isPrimitive = options.length > 0 && (typeof options[0] === "string" || typeof options[0] === "number");
  const selectedSet = useMemo(() => new Set(value), [value]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((opt) => getOptionLabel(opt).toLowerCase().includes(q));
  }, [options, search, getOptionLabel]);

  const handleToggle = (opt) => {
    if (isPrimitive) {
      const next = selectedSet.has(opt) ? value.filter((v) => v !== opt) : [...value, opt];
      onChange(next);
      return;
    }
    const id = getOptionValue(opt);
    const next = selectedSet.has(id) ? value.filter((v) => Number(v) !== Number(id)) : [...value, id];
    onChange(next);
  };

  const isSelected = (opt) =>
    isPrimitive ? selectedSet.has(opt) : selectedSet.has(getOptionValue(opt));

  return (
    <Box>
      <Box
        onClick={() => onToggleOpen(openKey === isOpen ? null : openKey)}
        sx={{
          minHeight: 40,
          px: 1.5,
          py: 0.75,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 0.5,
          bgcolor: "background.paper",
          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, flex: 1, minWidth: 0 }}>
          {value.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {placeholder}
            </Typography>
          )}
          {value.length > 0 &&
            (isPrimitive
              ? value.map((v) => (
                  <Chip key={v} label={v} size="small" variant="outlined" onDelete={() => handleToggle(v)} />
                ))
              : value.map((id) => {
                  const opt = options.find((o) => Number(getOptionValue(o)) === Number(id));
                  const lbl = opt ? getOptionLabel(opt) : id;
                  return (
                    <Chip
                      key={id}
                      label={lbl}
                      size="small"
                      variant="outlined"
                      onDelete={() => opt && handleToggle(opt)}
                    />
                  );
                }))}
        </Box>
        {isOpen === openKey ? (
          <ExpandLessIcon fontSize="small" color="action" />
        ) : (
          <ExpandMoreIcon fontSize="small" color="action" />
        )}
      </Box>
      <Collapse in={isOpen === openKey}>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderTop: 0,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            maxHeight: 220,
            overflowY: "auto",
            bgcolor: "background.default",
          }}
        >
          {options.length > 10 && (
            <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: "divider" }}>
              <InputBase
                size="small"
                fullWidth
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                startAdornment={<SearchIcon sx={{ mr: 0.5, color: "action.active" }} fontSize="small" />}
                sx={{ fontSize: "0.875rem" }}
              />
            </Box>
          )}
          {filteredOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
              Nenhuma opção
            </Typography>
          ) : (
            filteredOptions.map((opt) => {
              const selected = isSelected(opt);
              const labelStr = getOptionLabel(opt);
              return (
                <ListItemButton
                  key={isPrimitive ? labelStr : getOptionValue(opt)}
                  dense
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(opt);
                  }}
                  selected={selected}
                  sx={{ py: 0.25 }}
                >
                  <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} disableRipple />
                  <ListItemText primary={labelStr} primaryTypographyProps={{ variant: "body2" }} />
                </ListItemButton>
              );
            })
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

const STATUS_OPTIONS = [
  "Pendente",
  "Em Atendimento",
  "Contato Futuro",
  "Concluída",
  "Perdida",
  "Cancelada",
];

const isChatBotUser = (u) => Number(u?.ura) === 1;

function flattenTypesTree(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => {
    const id = n.id;
    const idPai = n.id_pai != null && n.id_pai !== "" ? Number(n.id_pai) : null;
    const nome = n.nome || String(id);
    return [
      { id: Number(id), id_pai: idPai, nome },
      ...flattenTypesTree(n.children || []),
    ];
  });
}

function normalizeIds(ids) {
  return [].concat(ids || []).map((x) => Number(x)).filter((n) => Number.isFinite(n));
}

function getDefaultDateRange() {
  const today = new Date();
  const past = new Date();
  past.setDate(today.getDate() - 30);

  const format = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return {
    start: format(past),
    end: format(today),
  };
}

const AdvancedSearchModal = ({
  open,
  onClose,
  initialQuery = "",
  initialAdvanced = {},
  onApply,
}) => {
  const [typesTree, setTypesTree] = useState([]);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [responsavelAnchor, setResponsavelAnchor] = useState(null);

  const [keyword, setKeyword] = useState("");
  const [statusFilters, setStatusFilters] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [selectedSubtype1Ids, setSelectedSubtype1Ids] = useState([]);
  const [selectedSubtype2Ids, setSelectedSubtype2Ids] = useState([]);
  const [selectedSubtype3Ids, setSelectedSubtype3Ids] = useState([]);
  const [selectedOriginIds, setSelectedOriginIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [tipoResponsavel, setTipoResponsavel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateField, setDateField] = useState("atualizacao");
  const [includeMessageText, setIncludeMessageText] = useState(false);
  const [openSelect, setOpenSelect] = useState(null);
  const [autoDateRangeApplied, setAutoDateRangeApplied] = useState(false);

  const loadOptions = useCallback(async () => {
    if (!open) return;
    setLoadingOptions(true);
    try {
      const [typesRes, channelsRes, usersRes, agentsRes] = await Promise.all([
        fetchData("/types?tree=1&limit=500", "GET"),
        fetchData("/channels?limit=200", "GET"),
        fetchData("/users?limit=200", "GET"),
        fetchData("/agents?limit=200", "GET"),
      ]);
      const raw = typesRes?.data;
      const roots = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.data?.data)
            ? raw.data.data
            : [];
      setTypesTree(roots);
      setChannels(Array.isArray(channelsRes?.data) ? channelsRes.data : []);
      setUsers(Array.isArray(usersRes?.data) ? usersRes.data : []);
      const agentsList = agentsRes?.data ?? agentsRes?.agents ?? [];
      setAgents(Array.isArray(agentsList) ? agentsList : []);
    } catch (e) {
      console.error("Erro ao carregar opções da busca avançada:", e);
      setTypesTree([]);
      setChannels([]);
      setUsers([]);
      setAgents([]);
    } finally {
      setLoadingOptions(false);
    }
  }, [open]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // Sincronizar com props apenas quando o dialog ABRE (não a cada re-render do pai).
  // Se rodar sempre que initialAdvanced mudar, a seleção do usuário é sobrescrita e "some".
  const prevOpenRef = React.useRef(false);
  useEffect(() => {
    if (!open) {
      setOpenSelect(null);
      setResponsavelAnchor(null);
      prevOpenRef.current = false;
      return;
    }
    const isOpening = !prevOpenRef.current;
    prevOpenRef.current = true;
    if (isOpening) {
      setKeyword(initialQuery || "");
      setStatusFilters(Array.isArray(initialAdvanced?.statusFilters) ? initialAdvanced.statusFilters : []);
      setSelectedTypeIds(normalizeIds(initialAdvanced?.ids_tipos));
      setSelectedSubtype1Ids(normalizeIds(initialAdvanced?.ids_subtipo1));
      setSelectedSubtype2Ids(normalizeIds(initialAdvanced?.ids_subtipo2));
      setSelectedSubtype3Ids(normalizeIds(initialAdvanced?.ids_subtipo3));
      setSelectedOriginIds(normalizeIds(initialAdvanced?.ids_origens));
      setSelectedUserIds(normalizeIds(initialAdvanced?.ids_usuarios));
      const adv = initialAdvanced || {};
      if (Array.isArray(adv.filters) && adv.filters.includes("Fila")) {
        setTipoResponsavel("fila");
      } else if (["humanos", "chatbots", "agentes"].includes(adv.tipo_responsavel)) {
        setTipoResponsavel(adv.tipo_responsavel);
      } else {
        setTipoResponsavel("");
      }
      setStartDate(initialAdvanced?.data_inicio || "");
      setEndDate(initialAdvanced?.data_fim || "");
      setDateField(initialAdvanced?.date_field === "entrada" ? "entrada" : "atualizacao");
      const includeMsg =
        initialAdvanced?.include_message_text === true ||
        String(initialAdvanced?.include_message_text || "").trim().toLowerCase() === "1" ||
        String(initialAdvanced?.include_message_text || "").trim().toLowerCase() === "true";
      setIncludeMessageText(includeMsg);
      setAutoDateRangeApplied(false);
    }
  }, [open, initialQuery, initialAdvanced]);

  const humanUsers = useMemo(() => (users || []).filter((u) => !isChatBotUser(u)), [users]);
  const chatbotUsers = useMemo(() => (users || []).filter(isChatBotUser), [users]);

  const typesFlat = useMemo(() => flattenTypesTree(typesTree), [typesTree]);
  const rootTypes = useMemo(
    () => typesFlat.filter((t) => t.id_pai == null || t.id_pai === 0),
    [typesFlat]
  );

  const subtype1Options = useMemo(() => {
    if (!selectedTypeIds.length) return [];
    const idSet = new Set(selectedTypeIds.map(Number).filter(Number.isFinite));
    return typesFlat.filter((t) => t.id_pai != null && idSet.has(Number(t.id_pai)));
  }, [typesFlat, selectedTypeIds]);

  const subtype2Options = useMemo(() => {
    if (!selectedSubtype1Ids.length) return [];
    const idSet = new Set(selectedSubtype1Ids.map(Number).filter(Number.isFinite));
    return typesFlat.filter((t) => t.id_pai != null && idSet.has(Number(t.id_pai)));
  }, [typesFlat, selectedSubtype1Ids]);

  const subtype3Options = useMemo(() => {
    if (!selectedSubtype2Ids.length) return [];
    const idSet = new Set(selectedSubtype2Ids.map(Number).filter(Number.isFinite));
    return typesFlat.filter((t) => t.id_pai != null && idSet.has(Number(t.id_pai)));
  }, [typesFlat, selectedSubtype2Ids]);

  const handleApply = () => {
    const filtersOut =
      tipoResponsavel === "fila" ? [...statusFilters, "Fila"] : statusFilters;
    const payload = {
      q: keyword.trim(),
      filters: filtersOut,
      data_inicio: startDate || undefined,
      data_fim: endDate || undefined,
      date_field: dateField,
      ids_tipos: selectedTypeIds.length ? selectedTypeIds : undefined,
      ids_subtipo1: selectedSubtype1Ids.length ? selectedSubtype1Ids : undefined,
      ids_subtipo2: selectedSubtype2Ids.length ? selectedSubtype2Ids : undefined,
      ids_subtipo3: selectedSubtype3Ids.length ? selectedSubtype3Ids : undefined,
      ids_origens: selectedOriginIds.length ? selectedOriginIds : undefined,
      ids_usuarios: selectedUserIds.length ? selectedUserIds : undefined,
      tipo_responsavel:
        tipoResponsavel && ["fila", "humanos", "chatbots", "agentes"].includes(tipoResponsavel)
          ? tipoResponsavel
          : undefined,
      include_message_text: includeMessageText || undefined,
    };
    if (typeof onApply === "function") onApply(payload);
    onClose();
  };

  const handleClearFilters = () => {
    setKeyword("");
    setStatusFilters([]);
    setSelectedTypeIds([]);
    setSelectedSubtype1Ids([]);
    setSelectedSubtype2Ids([]);
    setSelectedSubtype3Ids([]);
    setSelectedOriginIds([]);
    setSelectedUserIds([]);
    setTipoResponsavel("");
    setStartDate("");
    setEndDate("");
    setDateField("atualizacao");
    setIncludeMessageText(false);
    setAutoDateRangeApplied(false);
  };

  const hasActiveFilters =
    keyword.trim() ||
    statusFilters.length > 0 ||
    selectedTypeIds.length > 0 ||
    selectedSubtype1Ids.length > 0 ||
    selectedSubtype2Ids.length > 0 ||
    selectedSubtype3Ids.length > 0 ||
    selectedOriginIds.length > 0 ||
    selectedUserIds.length > 0 ||
    tipoResponsavel !== "" ||
    startDate ||
    endDate ||
    includeMessageText;

  const sectionSx = {
    p: 2,
    borderRadius: 2,
    bgcolor: "action.hover",
    "&:empty": { display: "none" },
  };
  const fieldLabelSx = { mb: 0.75, fontWeight: 500, color: "text.secondary" };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: 24,
        },
      }}
      disableScrollLock
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          fontWeight: 600,
          fontSize: "1.25rem",
          borderBottom: 1,
          borderColor: "divider",
          py: 2,
          px: 3,
          flexShrink: 0,
        }}
      >
        <TuneRoundedIcon color="primary" />
        Busca avançada
      </DialogTitle>

      <DialogContent
        sx={{
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          px: 0,
          pt: 0,
          pb: 0,
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        {loadingOptions ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box
            sx={{
              overflowY: "auto",
              overflowX: "hidden",
              px: 3,
              pt: 3,
              pb: 2,
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Seção: Busca por texto — sempre visível no topo, evita corte */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ ...fieldLabelSx, display: "block" }}>
                Palavra-chave
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Digite termos para buscar..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Box>

            <Grid container spacing={3}>
              {/* Coluna esquerda: Status e Tipo */}
              <Grid item xs={12} md={6}>
                <Box sx={sectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "text.primary" }}>
                    Status e tipo
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Status</Typography>
                      <InlineMultiSelect
                        placeholder="Selecione os status..."
                        options={STATUS_OPTIONS}
                        value={statusFilters}
                        onChange={setStatusFilters}
                        getOptionLabel={(opt) => String(opt)}
                        openKey="status"
                        isOpen={openSelect}
                        onToggleOpen={setOpenSelect}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Responsável</Typography>
                      <Box
                        onClick={(e) => setResponsavelAnchor(e.currentTarget)}
                        sx={{
                          minHeight: 40,
                          px: 1.5,
                          py: 0.75,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 0.5,
                          bgcolor: "background.paper",
                          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                        }}
                      >
                        <Typography variant="body2" color={tipoResponsavel || selectedUserIds.length ? "text.primary" : "text.secondary"}>
                          {tipoResponsavel === "fila"
                            ? "Fila (sem atribuição)"
                            : tipoResponsavel === "agentes"
                            ? "Agentes de IA"
                            : tipoResponsavel === "humanos"
                            ? "Humanos"
                            : tipoResponsavel === "chatbots"
                            ? "ChatBots"
                            : selectedUserIds.length > 0
                            ? selectedUserIds.length === 1
                              ? (users.find((u) => Number(u.id) === Number(selectedUserIds[0]))?.nome || users.find((u) => Number(u.id) === Number(selectedUserIds[0]))?.name || `Usuário ${selectedUserIds[0]}`)
                              : `${selectedUserIds.length} usuários`
                            : "Todos"}
                        </Typography>
                        <ExpandMoreIcon fontSize="small" color="action" />
                      </Box>
                      <Menu
                        anchorEl={responsavelAnchor}
                        open={Boolean(responsavelAnchor)}
                        onClose={() => setResponsavelAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        PaperProps={{ sx: { maxHeight: 400, minWidth: 280 } }}
                      >
                        <MenuItem
                          onClick={() => {
                            setTipoResponsavel("");
                            setSelectedUserIds([]);
                            setResponsavelAnchor(null);
                          }}
                        >
                          Todos
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setTipoResponsavel("fila");
                            setSelectedUserIds([]);
                            setResponsavelAnchor(null);
                          }}
                        >
                          Fila (sem atribuição)
                        </MenuItem>
                        {agents.length > 0 && [
                          <ListSubheader key="sh-agent" sx={{ lineHeight: 2, fontSize: "0.75rem", opacity: 0.8 }}>
                            — Agentes de IA —
                          </ListSubheader>,
                          <MenuItem
                            key="opt-agentes"
                            onClick={() => {
                              setTipoResponsavel("agentes");
                              setSelectedUserIds([]);
                              setResponsavelAnchor(null);
                            }}
                          >
                            Agentes de IA
                          </MenuItem>,
                        ]}
                        {humanUsers.length > 0 && [
                          <ListSubheader key="sh-humanos" sx={{ lineHeight: 2, fontSize: "0.75rem", opacity: 0.8 }}>
                            — Usuários —
                          </ListSubheader>,
                          <MenuItem
                            key="opt-humanos"
                            onClick={() => {
                              setTipoResponsavel("humanos");
                              setSelectedUserIds([]);
                              setResponsavelAnchor(null);
                            }}
                          >
                            Humanos (qualquer)
                          </MenuItem>,
                          ...humanUsers.map((u) => {
                            const id = Number(u.id);
                            const checked = selectedUserIds.includes(id);
                            return (
                              <MenuItem
                                key={`u-${id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTipoResponsavel("");
                                  setSelectedUserIds((prev) =>
                                    checked ? prev.filter((x) => Number(x) !== id) : [...prev, id]
                                  );
                                }}
                              >
                                <Checkbox size="small" checked={checked} sx={{ mr: 1, p: 0 }} />
                                <ListItemText primary={u.nome ?? u.name ?? `Usuário ${id}`} />
                              </MenuItem>
                            );
                          }),
                        ]}
                        {chatbotUsers.length > 0 && [
                          <ListSubheader key="sh-chatbots" sx={{ lineHeight: 2, fontSize: "0.75rem", opacity: 0.8 }}>
                            — ChatBots —
                          </ListSubheader>,
                          <MenuItem
                            key="opt-chatbots"
                            onClick={() => {
                              setTipoResponsavel("chatbots");
                              setSelectedUserIds([]);
                              setResponsavelAnchor(null);
                            }}
                          >
                            ChatBots (qualquer)
                          </MenuItem>,
                          ...chatbotUsers.map((u) => {
                            const id = Number(u.id);
                            const checked = selectedUserIds.includes(id);
                            return (
                              <MenuItem
                                key={`cb-${id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTipoResponsavel("");
                                  setSelectedUserIds((prev) =>
                                    checked ? prev.filter((x) => Number(x) !== id) : [...prev, id]
                                  );
                                }}
                              >
                                <Checkbox size="small" checked={checked} sx={{ mr: 1, p: 0 }} />
                                <ListItemText primary={`${u.nome ?? u.name ?? `ChatBot ${id}`} (ChatBot)`} />
                              </MenuItem>
                            );
                          }),
                        ]}
                      </Menu>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Tipo</Typography>
                      <InlineMultiSelect
                        placeholder="Selecione os tipos..."
                        options={rootTypes}
                        value={selectedTypeIds}
                        onChange={(ids) => {
                          setSelectedTypeIds(ids);
                          if (!ids.length) {
                            setSelectedSubtype1Ids([]);
                            setSelectedSubtype2Ids([]);
                            setSelectedSubtype3Ids([]);
                          }
                        }}
                        getOptionLabel={(o) => o?.nome ?? String(o?.id ?? "")}
                        getOptionValue={(o) => Number(o?.id)}
                        openKey="type"
                        isOpen={openSelect}
                        onToggleOpen={setOpenSelect}
                        searchPlaceholder="Filtrar tipos..."
                      />
                    </Box>
                    {subtype1Options.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={fieldLabelSx}>Subtipo 1</Typography>
                        <InlineMultiSelect
                          placeholder="Selecione..."
                          options={subtype1Options}
                          value={selectedSubtype1Ids}
                          onChange={(ids) => {
                            setSelectedSubtype1Ids(ids);
                            if (!ids.length) {
                              setSelectedSubtype2Ids([]);
                              setSelectedSubtype3Ids([]);
                            }
                          }}
                          getOptionLabel={(o) => o?.nome ?? String(o?.id ?? "")}
                          getOptionValue={(o) => Number(o?.id)}
                          openKey="subtype1"
                          isOpen={openSelect}
                          onToggleOpen={setOpenSelect}
                          searchPlaceholder="Filtrar..."
                        />
                      </Box>
                    )}
                    {subtype2Options.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={fieldLabelSx}>Subtipo 2</Typography>
                        <InlineMultiSelect
                          placeholder="Selecione..."
                          options={subtype2Options}
                          value={selectedSubtype2Ids}
                          onChange={(ids) => {
                            setSelectedSubtype2Ids(ids);
                            if (!ids.length) setSelectedSubtype3Ids([]);
                          }}
                          getOptionLabel={(o) => o?.nome ?? String(o?.id ?? "")}
                          getOptionValue={(o) => Number(o?.id)}
                          openKey="subtype2"
                          isOpen={openSelect}
                          onToggleOpen={setOpenSelect}
                          searchPlaceholder="Filtrar..."
                        />
                      </Box>
                    )}
                    {subtype3Options.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={fieldLabelSx}>Subtipo 3</Typography>
                        <InlineMultiSelect
                          placeholder="Selecione..."
                          options={subtype3Options}
                          value={selectedSubtype3Ids}
                          onChange={setSelectedSubtype3Ids}
                          getOptionLabel={(o) => o?.nome ?? String(o?.id ?? "")}
                          getOptionValue={(o) => Number(o?.id)}
                          openKey="subtype3"
                          isOpen={openSelect}
                          onToggleOpen={setOpenSelect}
                          searchPlaceholder="Filtrar..."
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Coluna direita: Canal, Período */}
              <Grid item xs={12} md={6}>
                <Box sx={sectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "text.primary" }}>
                    Origem e período
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Canal / Origem</Typography>
                      <InlineMultiSelect
                        placeholder="Selecione os canais..."
                        options={channels}
                        value={selectedOriginIds}
                        onChange={setSelectedOriginIds}
                        getOptionLabel={(o) => o?.nome ?? o?.name ?? String(o?.id ?? "")}
                        getOptionValue={(o) => Number(o?.id)}
                        openKey="channel"
                        isOpen={openSelect}
                        onToggleOpen={setOpenSelect}
                        searchPlaceholder="Filtrar canais..."
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={fieldLabelSx}>Data de referência</Typography>
                      <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                        <InputLabel>Referência</InputLabel>
                        <Select
                          value={dateField}
                          onChange={(e) => setDateField(e.target.value)}
                          label="Referência"
                        >
                          <MenuItem value="atualizacao">Última atualização</MenuItem>
                          <MenuItem value="entrada">Data de entrada</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          label="De"
                          type="date"
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Até"
                          type="date"
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                    </Grid>
                    {autoDateRangeApplied && (
                      <Typography
                        variant="caption"
                        sx={{ mt: 0.5 }}
                        color="error"
                      >
                        Aplicamos automaticamente um filtro de data nos últimos 30 dias para
                        deixar esta busca mais leve. Ajuste o período se quiser ampliar ou
                        reduzir o alcance.
                      </Typography>
                    )}
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Checkbox
                          size="small"
                          checked={includeMessageText}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setIncludeMessageText(checked);
                            if (checked && !startDate && !endDate) {
                              const range = getDefaultDateRange();
                              setStartDate(range.start);
                              setEndDate(range.end);
                              setAutoDateRangeApplied(true);
                            }
                          }}
                        />
                        <Box>
                          <Typography variant="subtitle2" sx={fieldLabelSx}>
                            Incluir texto das mensagens
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ao ativar esta opção, a busca passa a analisar também o conteúdo completo
                            das mensagens da conversa. Em históricos muitos grandes isso torna a pesquisa
                            mais pesada e pode reduzir a velocidade de resposta da tela.
                            Para manter um bom desempenho, sugerimos um período recente por padrão, mas
                            você pode ajustar as datas acima conforme a sua necessidade.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: "divider",
          px: 3,
          py: 2,
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {hasActiveFilters && (
          <Button onClick={handleClearFilters} color="inherit" size="medium">
            Limpar filtros
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} size="medium">
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          size="medium"
          startIcon={<SearchRoundedIcon />}
        >
          Pesquisar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedSearchModal;
